import { gql } from '@apollo/client'

const GAZETTEERS_FRAGMENT = gql`
  fragment GazetteersFragment on SearchResult {
    groups {
      dateRange {
        fromTimestamp
        toTimestamp
      }
      gazetteerImages: imageApids(limit: 1)
      id
      places(extraLocale: $extraLocale) {
        fullName
        localizedFullName
      }
      title
    }
    totalCount
  }
`
const ADVANCED_SEARCH_FRAGMENT = gql`
  ${GAZETTEERS_FRAGMENT}
  fragment AdvancedSearchFragment on SearchResult {
    gazetteers {
      ...GazetteersFragment
    }
    groups {
      ancestors
      ancestralHall
      authors
      childGroupCount
      clanBranch @skip(if: $multipleClanBranches)
      clanBranches @include(if: $multipleClanBranches)
      clans
      coverages {
        place {
          fullName
        }
        recordType {
          name
        }
      }
      dateRange {
        fromTimestamp
        toTimestamp
      }
      id
      imageApids(limit: 1)
      places(extraLocale: $extraLocale) {
        fullName
        localizedFullName
      }
      publicationDate
      romanizations {
        name
        values
      }
      migrantAncestor
      titleNumber
      totalVolumes
      volumeSetTitle
      volumes
    }
    id
    selectedFields {
      places {
        fullName
        id
        latitude
        longitude
        name
        parents {
          id
          name
        }
      }
    }
    totalCount
    whereFacets {
      count
      place {
        fromYear
        id
        latitude
        longitude
        name
        toYear
      }
    }
  }
`

export const advancedSearch = gql`
  ${ADVANCED_SEARCH_FRAGMENT}
  query VolumeSetAdvancedSearch(
    $chineseConversionInput: [String!]
    $extraLocale: String
    $gazetteerSearch: Boolean
    $multipleClanBranches: Boolean!
    $searchInput: SearchInput!
  ) {
    chineseConversions(input: $chineseConversionInput) {
      simplified
      traditional
    }

    volumeSetSearch(fallbackToGazetteerSearch: $gazetteerSearch, searchInput: $searchInput) {
      ...AdvancedSearchFragment
      parentPlaceUnknownTotalCount
      unknownTotalCount
    }
  }
`

export const basicSearch = gql`
  query VolumeSetBasicSearch($extraLocale: String, $searchInput: SearchInput!) {
    volumeSetSearch(searchInput: $searchInput) {
      groups {
        ancestors
        ancestralHall
        authors
        childGroupCount
        coverages {
          place {
            fullName
          }
        }
        id
        places(extraLocale: $extraLocale) {
          fullName
          localizedFullName
        }
        publicationDate
        romanizations {
          name
          values
        }
        titleNumber
        volumeSetTitle
      }
      id
      parentPlaceUnknownTotalCount
      selectedFields {
        places {
          boundaryUrl
          hasNoChildren
          id
          latitude
          longitude
          name
          parents(countForFirstOnly: true, includeSearchResultCounts: true) {
            count
            id
            name
          }
          zoomLevel
        }
      }
      totalCount
      unknownTotalCount
      whereFacets {
        count
        place {
          fromYear
          fullName
          id
          latitude
          localizedType
          longitude
          name
          toYear
          type
          zoomLevel
        }
      }
    }
  }
`

export const chineseConversionQuery = gql`
  query ChineseConversionQuery($input: [String!]) {
    chineseConversions(input: $input) {
      simplified
      traditional
    }
  }
`

export const downloadJiapuSearchResults = gql`
  query DownloadJiapuSearchResults($searchInput: SearchInput!) {
    volumeSetSearch(searchInput: $searchInput) {
      groups {
        ancestors
        ancestralHall
        authors
        childCount
        childGroupCount
        coverages {
          place {
            fullName
          }
        }
        generationPoem
        id
        imageApids(limit: 1)
        properties(names: ["volume"]) {
          name
          values
        }
        publicationDate
        surnames
        titleNotes
        titleNumber
        volumeSetTitle
        yearRange {
          fromYear
          toYear
        }
      }
      nextPageToken
      totalCount
    }
  }
`

export const downloadJokboSearchResults = gql`
  query DownloadJokboSearchResults($multipleClanBranches: Boolean!, $searchInput: SearchInput!) {
    volumeSetSearch(searchInput: $searchInput) {
      groups {
        ancestors
        authors
        childCount
        childGroupCount
        clanBranch @skip(if: $multipleClanBranches)
        clanBranches @include(if: $multipleClanBranches)
        clans
        coverages {
          place {
            fullName
          }
        }
        id
        imageApids(limit: 1)
        properties(names: ["volume"]) {
          name
          values
        }
        publicationDate
        surnames
        titleNotes
        titleNumber
        volumeSetTitle
        yearRange {
          fromYear
          toYear
        }
      }
      nextPageToken
      totalCount
    }
  }
`

export const gazetteersSearch = gql`
  ${GAZETTEERS_FRAGMENT}
  query GazetteersSearch($extraLocale: String, $searchInput: SearchInput!) {
    volumeSetSearch(searchInput: $searchInput) {
      ...GazetteersFragment
    }
  }
`

export const groupIdSearch = gql`
  query Groups($extraLocale: String, $groupIds: [String!]!, $multipleClanBranches: Boolean!) {
    groups(groupIds: $groupIds) {
      ancestors
      ancestralHall
      authors
      childGroupCount
      clanBranch @skip(if: $multipleClanBranches)
      clanBranches @include(if: $multipleClanBranches)
      clans
      coverages {
        place(extraLocale: $extraLocale) {
          fullName
          localizedFullName
        }
      }
      id
      publicationDate
      romanizations {
        name
        values
      }
      titleNumber
      volumeSetTitle
    }
  }
`

export const previewPane = gql`
  query PreviewPane($groupId: String!, $multipleClanBranches: Boolean!) {
    group(groupIdOrArkId: $groupId) {
      ancestors
      ancestralHall
      authors
      childGroupCount
      clanBranch @skip(if: $multipleClanBranches)
      clanBranches @include(if: $multipleClanBranches)
      clans
      coverages {
        place {
          fullName
        }
      }
      generationPoem
      imageApids(previews: true)
      publicationDate
      surnames
      titleNotes
    }
  }
`

export const unknownSearch = gql`
  ${GAZETTEERS_FRAGMENT}
  query UnknownGroups($extraLocale: String, $gazetteerSearch: Boolean, $searchInput: SearchInput!) {
    volumeSetSearch(
      fallbackToGazetteerSearch: $gazetteerSearch
      includeFullTotalCountAndFacets: true
      searchInput: $searchInput
    ) {
      ...GazetteersFragment
      fullTotalCount
      fullWhereFacets {
        count
        place {
          fromYear
          fullName
          id
          latitude
          longitude
          name
          toYear
          type
        }
      }
      groups {
        ancestors
        ancestralHall
        authors
        childGroupCount
        coverages {
          place(extraLocale: $extraLocale) {
            fullName
            localizedFullName
          }
        }
        generationPoem
        id
        imageApids(previews: true)
        publicationDate
        romanizations {
          name
          values
        }
        surnames
        titleNumber
        volumeSetTitle
      }
      selectedFields {
        places {
          fullName
          hasNoChildren
          id
          latitude
          longitude
          name
          parents(countForFirstOnly: true, includeSearchResultCounts: true) {
            count
            id
            name
          }
        }
      }
      totalCount
    }
  }
`

export const volumeSetsByImageGroupNumber = gql`
  query VolumeSetsByImageGroupNumber($imageGroupNumber: String!) {
    volumeSetsByImageGroupNumber(imageGroupNumber: $imageGroupNumber) {
      firstChildImageApid
      volumeSetGroupId
    }
  }
`
